import React, { useEffect, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import '../../styles/formRegistro.css';
import { Topbar } from '../topbar';
import { Banner } from '../banner';
import { TimePick } from '../lib/TimePick';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { useReservation } from '../../contexts/ReservationContext';
import { useNavigate } from 'react-router-dom';

export const FormRegistro = () => {
  const { t } = useTranslation();
  const { reservation, setReservation } = useReservation();
  const navigate = useNavigate();
  const formDataRef = useRef({});

  // ⬇️ Defaults de todos tus campos
  const formDefaults = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    airbnb: '',
    airlineArrival: '',
    flightNumberArrival: '',
    arrivalHourArrival: '',
    airlineDeparture: '',
    flightNumberDeparture: '',
    arrivalHourDeparture: ''
  };

  const savedFormData = JSON.parse(localStorage.getItem('formRegistroData') || '{}');

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    control,
    reset,
    setValue
  } = useForm({
    mode: 'onChange',
    defaultValues: { ...formDefaults, ...savedFormData } // ⬅️ Cargar form con valores guardados
  });

  const resp = reservation?.cJSON || reservation;
  const { tarifa, vehiculo, tripData } = reservation || {};
  // Guarda en localStorage en cada cambio
  useEffect(() => {
    const subscription = watch((value) => {
      formDataRef.current = {
        ...value,
        hotel_s: resp?.Hotel,
        airport_s: resp?.airport,
        total_s: resp?.PagoTotal,
        passenger_s: resp?.pax,
        departure_date_s: resp?.departure_date,
        arrival_date_s: resp?.arrival_date,
        type_trip_s: resp?.type_trip,
        zone_s: resp?.zone_id,
      };
      localStorage.setItem('formRegistroData', JSON.stringify(formDataRef.current));
    });
    return () => subscription.unsubscribe();
  }, [watch, resp]);

  // Render PayPal
  useEffect(() => {
    if (!tarifa?.price) return;

    const loadPayPalScript = () => {
      if (document.getElementById('paypal-sdk')) return renderPayPalButton();

      const script = document.createElement('script');
      script.id = 'paypal-sdk';
      script.src = `https://www.paypal.com/sdk/js?client-id=${process.env.REACT_APP_PAYPAL_CLIENT_ID}&currency=USD`;
      script.async = true;
      script.onload = renderPayPalButton;
      document.body.appendChild(script);
    };

    const renderPayPalButton = () => {
      const container = document.getElementById('paypal-button-container');
      if (!container) return;

      container.innerHTML = '';
      window.paypal.Buttons({
        style: { layout: 'vertical', color: 'gold', shape: 'rect', label: 'paypal' },
        createOrder: (data, actions) => {
          return actions.order.create({
            purchase_units: [{
              amount: {
                value: parseFloat(tarifa.price).toFixed(2),
                currency_code: 'USD'
              }
            }]
          });
        },
        onApprove: async (data, actions) => {
          const details = await actions.order.capture();
          const postData = { ...formDataRef.current, payments: details };

          try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}create`, { data: postData });
            if (!response.data.bError && !response.data.lError) {
              setReservation(response.data.data);
              localStorage.removeItem('formRegistroData'); // ⬅️ Limpiar localStorage
              Swal.fire({ title: 'Reserva completada', icon: 'success' }).then(() => navigate('/Reservation'));
            } else {
              Swal.fire({ title: 'Error', text: response.data.cMensagge, icon: 'error' });
            }
          } catch (err) {
            Swal.fire({ title: 'Error', text: 'Error al guardar la reserva.', icon: 'error' });
          }
        }
      }).render('#paypal-button-container');
    };

    loadPayPalScript();
  }, [tarifa]);

  const onSubmit = async (data) => {
    const finalData = formDataRef.current;
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}create`, { data: finalData });
      if (!response.data.bError && !response.data.lError) {
        setReservation(response.data.data);
        localStorage.removeItem('formRegistroData'); // ⬅️ Limpiar localStorage
        Swal.fire({ title: 'Reserva completada', icon: 'success' }).then(() => navigate('/Reservation'));
      } else {
        Swal.fire({ title: 'Error', text: response.data.cMensagge, icon: 'error' });
      }
    } catch {
      Swal.fire({ title: 'Error', text: 'Ocurrió un error al guardar la reserva.', icon: 'error' });
    }
  };

  useEffect(() => {
    if (tripData?.airport) {
      setValue('airport', tripData.airport);
    }
  }, [tripData?.airport, setValue]);

  if (!reservation) {
    return <div className="text-center mt-5">Cargando información de la reserva...</div>;
  }

  return (
    <>
      <Topbar />
      <Banner />
      <div className="container">
        <div className="contact__wrapper shadow-lg mt-n9">
          <div className="row no-gutters">
            {/* Información reserva */}
            <div className="col-lg-5 contact-info__wrapper gradient-brand-color p-5 order-lg-2">
              <h2 className='text-white'>{t('formRegistro.information')}</h2>
              <ul className="contact-info__list list-style--none position-relative z-index-101">
                {resp?.Hotel && <li className="mb-4"><i className="fas fa-plane"></i> Hotel: {resp.Hotel}</li>}
                {resp?.PagoTotal && <li className="mb-4"><i className="fas fa-money-bill-alt"></i> Total: ${resp.PagoTotal} USD</li>}
                {resp?.pax && <li className="mb-4"><i className="fas fa-user"></i> {t('formRegistro.passengers')}: {resp.pax}</li>}
                {vehiculo && <li className="mb-4"><i className="fas fa-car"></i> Vehículo: {vehiculo.nombre}</li>}
              </ul>
            </div>

            {/* Formulario */}
            <div className="col-lg-7 contact-form__wrapper p-5 order-lg-1">
              <form onSubmit={handleSubmit(onSubmit)} onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}>
                <div className="row">
                  {/* Datos personales */}
                  <input type="hidden" {...register('airport')} />
                  <div className="col-sm-6 mb-3">
                    <label className="required-field">{t('formRegistro.first_name')}</label>
                    <input className="form-control" {...register('firstName', { required: 'Campo obligatorio' })} />
                    {errors.firstName && <p className='text-danger'>{errors.firstName.message}</p>}
                  </div>

                  <div className="col-sm-6 mb-3">
                    <label>{t('formRegistro.last_name')}</label>
                    <input className="form-control" {...register('lastName', { required: 'Campo obligatorio' })} />
                    {errors.lastName && <p className='text-danger'>{errors.lastName.message}</p>}
                  </div>

                  <div className="col-sm-6 mb-3">
                    <label className="required-field">Email</label>
                    <input className="form-control" {...register('email', {
                      required: 'Email obligatorio',
                      pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Email inválido' }
                    })} />
                    {errors.email && <p className='text-danger'>{errors.email.message}</p>}
                  </div>

                  <div className="col-sm-6 mb-3">
                    <label>Phone Number</label>
                    <input className="form-control" {...register('phone', { required: 'Campo obligatorio' })} />
                    {errors.phone && <p className='text-danger'>{errors.phone.message}</p>}
                  </div>

                  {/* Dirección */}
                  <div className="col-sm-12 mb-3">
                    <label>Arrival / Pickup place / Hotel / AirB&B</label>
                    <input className="form-control" {...register('airbnb', { required: 'Campo obligatorio' })} />
                    {errors.airbnb && <p className='text-danger'>{errors.airbnb.message}</p>}
                  </div>

                  {/* Vuelo llegada */}
                  {(resp?.type_trip == 1 || resp?.type_trip == 2) && (
                    <>
                      <div className="col-sm-12 mb-3"><label>Airline</label><input className="form-control" {...register('airlineArrival')} /></div>
                      <div className="col-sm-12 mb-3"><label>Flight Number</label><input className="form-control" {...register('flightNumberArrival')} /></div>
                      <div className="col-sm-12 mb-3"><label>Arrival Hour</label>
                        <Controller
                          name="arrivalHourArrival"
                          control={control}
                          render={({ field }) => (
                            <TimePick
                              {...field}
                              value={field.value}
                              className="form-control"
                              intervals={15}
                            />
                          )}
                        />

                      </div>
                    </>
                  )}

                  {/* Vuelo salida */}
                  {(resp?.type_trip == 1 || resp?.type_trip == 3) && (
                    <>
                      <div className="col-sm-12 mb-3"><label>Airline</label><input className="form-control" {...register('airlineDeparture')} /></div>
                      <div className="col-sm-12 mb-3"><label>Flight Number</label><input className="form-control" {...register('flightNumberDeparture')} /></div>
                      <div className="col-sm-12 mb-4"><label>Departure Hour</label>
                        <Controller
                          name="arrivalHourDeparture"
                          control={control}
                          render={({ field }) => (
                            <TimePick
                              {...field}
                              value={field.value}
                              className="form-control"
                              intervals={15}
                            />
                          )}
                        />

                      </div>
                    </>
                  )}

                  {/* Botones */}
                  <div className="col-sm-12 mb-3">
                    <button type="submit" className="btn btn-primary btn-lg col-12">
                      Pay Upon Arrival <i className="fas fa-money-bill-alt"></i>
                    </button>
                  </div>
                  <div className="col-sm-12 mb-3" id="paypal-button-container"></div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
